import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { HiSearch, HiX, HiArrowLeft } from 'react-icons/hi';
import { HiOutlineUserGroup, HiOutlineCalendar, HiOutlineUser, HiArrowSmRight } from 'react-icons/hi';
import { ChannelListItem, EventListItem } from '../../../types';
import { UserSearchItem } from '../../../types/service.types';
import { generateCommunityUrl, generateEventUrl, generateProfileUrl } from '../../../utils/urlSlug';
import { useGeneral } from '../../../contexts';
import Services from '../../../services/Services';
import ChannelInitialIcon from '../ChannelInitialIcon/ChannelInitialIcon';
import { useAnimatedVisibility } from '../../../hooks/useAnimatedVisibility';
import styles from './SearchModal.module.css';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
}

type SearchType = 'channels' | 'events' | 'users';

interface UnifiedResults {
  channels: ChannelListItem[];
  events: EventListItem[];
  users: UserSearchItem[];
}

const emptyResults: UnifiedResults = { channels: [], events: [], users: [] };

const formatUnixSecondsToShortDate = (unixSeconds?: number | string): string | null => {
  if (unixSeconds === undefined || unixSeconds === null) return null;
  const seconds = typeof unixSeconds === 'string' ? Number(unixSeconds) : unixSeconds;
  if (!Number.isFinite(seconds)) return null;
  const d = new Date(seconds * 1000);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose, token }) => {
  const navigate = useNavigate();
  const { language } = useGeneral();
  const t = language.getTranslation;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<SearchType | null>(null);
  const [results, setResults] = useState<UnifiedResults>(emptyResults);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const searchDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      setSearchQuery('');
      setSearchType(null);
      setResults(emptyResults);
      setIsSearching(false);
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, []);

  // Unified search function
  const searchAll = useCallback(async (query: string, type: SearchType | null) => {
    if (!token || !query.trim() || query.length < 2) {
      setResults(emptyResults);
      return;
    }

    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;
    setIsSearching(true);

    try {
      const response = await Services.SocialHiveSearch(
        token,
        query,
        type || undefined,
        abortController
      );

      if (abortController.signal.aborted || !isMountedRef.current) return;

      if (response.data.status && response.data.result) {
        const r = response.data.result;
        setResults({
          channels: r.channels || [],
          events: r.events || [],
          users: r.users || [],
        });
      } else {
        setResults(emptyResults);
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') return;
      if (isMountedRef.current) {
        setResults(emptyResults);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setIsSearching(false);
      }
    }
  }, [token]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    if (isOpen && searchQuery.trim().length >= 2) {
      searchDebounceTimerRef.current = setTimeout(() => {
        searchAll(searchQuery, searchType);
      }, 300);
    } else {
      setResults(emptyResults);
    }

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery, searchType, isOpen, searchAll]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If a type is selected and no query, go back to quick links first
        if (searchType && !searchQuery) {
          setSearchType(null);
          setResults(emptyResults);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, searchType, searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleQuickLinkClick = (type: SearchType) => {
    setSearchType(type);
    setSearchQuery('');
    setResults(emptyResults);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 50);
  };

  const handleBackToQuickLinks = () => {
    setSearchType(null);
    setSearchQuery('');
    setResults(emptyResults);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 50);
  };

  const handleCommunityClick = (community: ChannelListItem) => {
    const communityTitle = community.channelTitle || community.title;
    navigate(generateCommunityUrl(communityTitle, community.channelID));
    onClose();
  };

  const handleEventClick = (event: EventListItem) => {
    const eventTitle = event.eventTitle || event.title;
    navigate(generateEventUrl(eventTitle, event.eventID));
    onClose();
  };

  const handleUserClick = (user: UserSearchItem) => {
    const userId = user.userID || user.id || '';
    navigate(generateProfileUrl(user.name, user.lastname, userId));
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const { shouldRender, isClosing, onAnimationEnd } = useAnimatedVisibility({
    isOpen,
    closeDuration: 200,
  });

  if (!shouldRender) return null;

  const hasAnyResults = results.channels.length > 0 || results.events.length > 0 || results.users.length > 0;
  const totalResultsCount = results.channels.length + results.events.length + results.users.length;
  const hasQuery = searchQuery.trim().length >= 2;
  const showResultsSection = hasQuery && (isSearching || hasAnyResults || !isSearching);
  const isIdle = !searchType && !hasQuery;

  // Dynamic placeholder based on selected type
  const placeholder = searchType
    ? t(`searchModal.placeholders.${searchType}`)
    : t('searchModal.placeholder');

  // Dynamic no-results message
  const noResultsMessage = searchType
    ? t(`searchModal.noResultsTyped.${searchType}`)
    : t('searchModal.noResults');

  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const keyboardShortcut = isMac ? '⌘K' : 'Ctrl+K';

  const modal = (
    <div
      className={`${styles.modalOverlay} ${isClosing ? styles.modalOverlayClosing : ''}`}
      onClick={handleOverlayClick}
      onAnimationEnd={onAnimationEnd}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`${styles.modalContent} ${isClosing ? styles.modalContentClosing : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className={`${styles.searchInputContainer} ${!showResultsSection && !isIdle && !hasQuery ? styles.searchInputContainerNoBorder : ''}`}>
          {searchType ? (
            <button
              className={styles.backButton}
              onClick={handleBackToQuickLinks}
            >
              {/* @ts-ignore */}
              <HiArrowLeft size={20} />
            </button>
          ) : (
            <>
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiSearch className={styles.searchIcon} size={20} />
            </>
          )}
          <input
            ref={searchInputRef}
            type="text"
            className={`${styles.searchInput} ${searchType ? styles.searchInputWithBack : ''}`}
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            autoFocus
          />
          <div className={styles.inputActions}>
            {searchQuery && (
              <button
                className={styles.clearButton}
                onClick={() => {
                  setSearchQuery('');
                  setResults(emptyResults);
                  searchInputRef.current?.focus();
                }}
                aria-label={t('searchModal.clearSearchAriaLabel')}
              >
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiX size={18} />
              </button>
            )}
            <div className={styles.keyboardShortcutHint}>
              <kbd className={styles.keyboardKey}>{keyboardShortcut}</kbd>
            </div>
          </div>
        </div>

        {/* Quick Links - shown when no type selected and no query */}
        {isIdle && (
          <div className={styles.quickLinksSection}>
            <button
              className={styles.quickLinkItem}
              onClick={() => handleQuickLinkClick('channels')}
            >
              <div className={styles.quickLinkIconWrapper}>
                {/* @ts-ignore */}
                <HiOutlineUserGroup size={20} />
              </div>
              <div className={styles.quickLinkInfo}>
                <div className={styles.quickLinkTitle}>{t('searchModal.quickLinks.discoverCommunities')}</div>
                <div className={styles.quickLinkSubtitle}>{t('searchModal.quickLinks.discoverCommunitiesSubtitle')}</div>
              </div>
              <div className={styles.quickLinkArrow}>
                {/* @ts-ignore */}
                <HiArrowSmRight size={20} />
              </div>
            </button>

            <button
              className={styles.quickLinkItem}
              onClick={() => handleQuickLinkClick('events')}
            >
              <div className={styles.quickLinkIconWrapper}>
                {/* @ts-ignore */}
                <HiOutlineCalendar size={20} />
              </div>
              <div className={styles.quickLinkInfo}>
                <div className={styles.quickLinkTitle}>{t('searchModal.quickLinks.discoverEvents')}</div>
                <div className={styles.quickLinkSubtitle}>{t('searchModal.quickLinks.discoverEventsSubtitle')}</div>
              </div>
              <div className={styles.quickLinkArrow}>
                {/* @ts-ignore */}
                <HiArrowSmRight size={20} />
              </div>
            </button>

            <button
              className={styles.quickLinkItem}
              onClick={() => handleQuickLinkClick('users')}
            >
              <div className={styles.quickLinkIconWrapper}>
                {/* @ts-ignore */}
                <HiOutlineUser size={20} />
              </div>
              <div className={styles.quickLinkInfo}>
                <div className={styles.quickLinkTitle}>{t('searchModal.quickLinks.discoverPeople')}</div>
                <div className={styles.quickLinkSubtitle}>{t('searchModal.quickLinks.discoverPeopleSubtitle')}</div>
              </div>
              <div className={styles.quickLinkArrow}>
                {/* @ts-ignore */}
                <HiArrowSmRight size={20} />
              </div>
            </button>
          </div>
        )}

        {/* Type selected but no query yet - show hint */}
        {searchType && !hasQuery && !isSearching && (
          <div className={styles.placeholderState}>
            <span>{placeholder}</span>
          </div>
        )}

        {/* Search Results */}
        {hasQuery && showResultsSection && (
          <div className={`${styles.resultsContainer} ${totalResultsCount > 4 ? styles.resultsContainerScrollable : ''}`}>
            {isSearching ? (
              <div className={styles.loadingState}>
                <span>{t('searchModal.searching')}</span>
              </div>
            ) : hasAnyResults ? (
              <div className={styles.resultsList}>
                {/* Communities Section */}
                {results.channels.length > 0 && (
                  <div className={styles.resultSection}>
                    {!searchType && <div className={styles.sectionHeader}>{t('searchModal.sections.communities')}</div>}
                    {results.channels.map((community) => {
                      const communityTitle = community.channelTitle || community.title;
                      const communityLogo = community.logo || community.channelLogo;
                      const userCount = community.channelUserCount || community.userCount || community.user_count || 0;
                      const isPublic = community.isPublic === "1";
                      return (
                        <button
                          key={community.channelID}
                          className={styles.resultItem}
                          onClick={() => handleCommunityClick(community)}
                        >
                          <div className={styles.resultItemContent}>
                            <div className={styles.resultItemIcon}>
                              {communityLogo ? (
                                <img
                                  src={communityLogo}
                                  alt={communityTitle}
                                  className={styles.resultItemLogo}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <ChannelInitialIcon
                                  channelName={communityTitle}
                                  size={48}
                                />
                              )}
                            </div>
                            <div className={styles.resultItemInfo}>
                              <div className={styles.resultItemHeader}>
                                <span className={styles.resultItemTitle}>{communityTitle}</span>
                                {isPublic ? (
                                  <span className={styles.resultItemBadge}>
                                    {t('searchModal.badges.public')}
                                  </span>
                                ) : (
                                  <span className={`${styles.resultItemBadge} ${styles.resultItemBadgePrivate}`}>
                                    {t('searchModal.badges.private')}
                                  </span>
                                )}
                              </div>
                              <div className={styles.resultItemMeta}>
                                <span className={styles.resultItemCount}>
                                  {t('searchModal.membersCount', { count: userCount })}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Events Section */}
                {results.events.length > 0 && (
                  <div className={styles.resultSection}>
                    {!searchType && <div className={styles.sectionHeader}>{t('searchModal.sections.events')}</div>}
                    {results.events.filter((e) => e.isPublic !== "3").map((event) => {
                      const eventTitle = event.eventTitle || event.title;
                      const cover = event.cover || event.image || event.bannerImage;
                      const channelTitle = event.channelTitle || event.channelDetails?.title;
                      const channelLogo = event.channelLogo || event.logo || event.channelDetails?.logo || null;
                      const startDate = formatUnixSecondsToShortDate(event.eventStartDateTime);
                      return (
                        <button
                          key={event.eventID}
                          className={styles.resultItem}
                          onClick={() => handleEventClick(event)}
                        >
                          <div className={styles.resultItemContent}>
                            <div className={`${styles.resultItemIcon} ${styles.eventResultThumbnail} ${styles.eventThumbnailWrapper}`}>
                              {cover ? (
                                <img
                                  src={cover}
                                  alt={eventTitle}
                                  className={styles.resultItemLogo}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <ChannelInitialIcon
                                  channelName={eventTitle}
                                  size={48}
                                />
                              )}
                              {channelLogo ? (
                                <div className={styles.eventChannelLogoOverlay}>
                                  <img
                                    src={channelLogo}
                                    alt={channelTitle || eventTitle}
                                    className={styles.eventChannelLogoImage}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              ) : null}
                            </div>
                            <div className={styles.resultItemInfo}>
                              <div className={styles.resultItemHeader}>
                                <span className={styles.resultItemTitle}>{eventTitle}</span>
                                {event.isOnline === "1" ? (
                                  <span className={`${styles.resultItemBadge} ${styles.resultItemBadgeOnline}`}>{t('searchModal.badges.online')}</span>
                                ) : (
                                  <span className={`${styles.resultItemBadge} ${styles.resultItemBadgeInPlace}`}>{t('searchModal.badges.inPlace')}</span>
                                )}
                              </div>
                              <div className={styles.resultItemMeta}>
                                {startDate ? (
                                  <span className={styles.resultItemCount}>{startDate}</span>
                                ) : null}
                                {channelTitle ? (
                                  <span className={styles.resultItemCount}>{channelTitle}</span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* People Section */}
                {results.users.length > 0 && (
                  <div className={styles.resultSection}>
                    {!searchType && <div className={styles.sectionHeader}>{t('searchModal.sections.people')}</div>}
                    {results.users.map((user) => {
                      const userId = user.userID || user.id || '';
                      const userAvatar = user.userAvatar || user.avatar;
                      const fullName = `${user.name || ''} ${user.lastname || ''}`.trim();
                      return (
                        <button
                          key={userId}
                          className={styles.resultItem}
                          onClick={() => handleUserClick(user)}
                        >
                          <div className={styles.resultItemContent}>
                            <div className={`${styles.resultItemIcon} ${styles.userAvatarIcon}`}>
                              {userAvatar ? (
                                <img
                                  src={userAvatar}
                                  alt={fullName}
                                  className={styles.resultItemLogo}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <ChannelInitialIcon
                                  channelName={fullName || 'U'}
                                  size={48}
                                />
                              )}
                            </div>
                            <div className={styles.resultItemInfo}>
                              <span className={styles.resultItemTitle}>{fullName}</span>
                              {user.title && (
                                <p className={styles.resultItemDescription}>{user.title}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <span>{noResultsMessage}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modal, document.body);
  }

  return modal;
};

export default SearchModal;
